/**
 * SuperAdmin → Respaldos (portado de Payroll360-SaaS).
 * Estado de respaldos por tenant, respaldo manual (uno o todos), descargar, restaurar,
 * eliminar, y la configuración global: programación, retención y destino (Azure).
 */
import React, { useState, useEffect, useCallback, Fragment } from 'react';
import SuperadminLayout from '../components/SuperadminLayout';
import { respaldosApi } from '../utils/api';
import AvisoRespaldoEmpresa from '../components/AvisoRespaldoEmpresa';
import {
  CircleStackIcon, ArrowPathIcon, Cog6ToothIcon, PlayIcon, ArrowDownTrayIcon,
  ArrowUturnLeftIcon, TrashIcon, ChevronDownIcon, ChevronUpIcon, CheckCircleIcon,
  XCircleIcon, CloudIcon, ClockIcon, ExclamationTriangleIcon, XMarkIcon
} from '@heroicons/react/24/outline';

const TIPO = { manual: 'Manual', automatico: 'Automático', antes_de_restaurar: 'Antes de restaurar' };
const FRECUENCIA = { daily: 'Diario', weekly: 'Semanal (domingos)', monthly: 'Mensual (día 1)' };

const formatBytes = (b) => {
  const n = Number(b) || 0;
  if (!n) return '-';
  const u = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), u.length - 1);
  return `${(n / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`;
};
const formatFecha = (d) => d
  ? new Date(d).toLocaleString('es-CR', { timeZone: 'America/Costa_Rica', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '-';

function Estado({ estado, error }) {
  if (estado === 'en_curso') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700"><ArrowPathIcon className="w-3 h-3 animate-spin" />En curso</span>;
  if (estado === 'fallido') return <span title={error || ''} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 cursor-help"><XCircleIcon className="w-3 h-3" />Fallido</span>;
  if (estado === 'exitoso') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700"><CheckCircleIcon className="w-3 h-3" />Exitoso</span>;
  return <span className="text-gray-500 text-xs">Sin respaldo</span>;
}

function Tarjeta({ icon: Icon, color, titulo, valor }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3">
      <div className={`p-2 rounded-lg ${color}`}><Icon className="w-5 h-5" /></div>
      <div className="min-w-0">
        <p className="text-sm text-gray-500">{titulo}</p>
        <p className="text-lg font-bold text-gray-900 truncate">{valor}</p>
      </div>
    </div>
  );
}

const inputCls = 'w-full bg-gray-100 border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500';

export default function Respaldos() {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [resumen, setResumen] = useState([]);
  const [logs, setLogs] = useState([]);
  const [showConfig, setShowConfig] = useState(false);
  const [form, setForm] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [expandido, setExpandido] = useState(null);
  const [ocupado, setOcupado] = useState(null); // tenantId | 'todos' | `dl-${id}`
  const [aviso, setAviso] = useState(null);     // { tipo: 'ok'|'error', texto }
  const [aRestaurar, setARestaurar] = useState(null);
  const [aEliminar, setAEliminar] = useState(null);
  const [seleccion, setSeleccion] = useState(() => new Set()); // ids de respaldos marcados en el detalle
  const [aEliminarVarios, setAEliminarVarios] = useState(null); // lista de respaldos a borrar juntos
  const [borrandoVarios, setBorrandoVarios] = useState(false);
  const [confirmarVencidos, setConfirmarVencidos] = useState(false);
  const [confirmacion, setConfirmacion] = useState('');
  const [analisis, setAnalisis] = useState(null);

  const avisar = (tipo, texto) => {
    setAviso({ tipo, texto });
    setTimeout(() => setAviso(null), 6000);
  };
  const errorDe = (e, def) => e.response?.data?.error || def;

  const cargar = useCallback(async (silencioso = false) => {
    if (!silencioso) setLoading(true);
    try {
      const [c, r, l] = await Promise.all([respaldosApi.config(), respaldosApi.resumen(), respaldosApi.logs({ limit: 300 })]);
      setConfig(c.data.config);
      setResumen(r.data.resumen || []);
      setLogs(l.data.logs || []);
      respaldosApi.analisis().then(a => setAnalisis(a.data.analisis)).catch(() => {});
    } catch (e) {
      if (!silencioso) avisar('error', errorDe(e, 'Error cargando respaldos'));
    } finally {
      if (!silencioso) setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const hayActividad = logs.some(l => l.estado === 'en_curso' || l.restauracion_estado === 'en_curso') || config?.lastStatus === 'running';
  useEffect(() => {
    if (!hayActividad) return;
    const t = setInterval(() => cargar(true), 5000);
    return () => clearInterval(t);
  }, [hayActividad, cargar]);

  const abrirConfig = () => {
    setForm({
      enabled: config.enabled,
      frequency: config.frequency,
      scheduleHour: config.scheduleHour,
      scheduleMinute: config.scheduleMinute,
      retentionDays: config.retentionDays,
      azureContainer: config.azureContainer,
      azureConnectionString: ''
    });
    setShowConfig(true);
  };

  const guardarConfig = async () => {
    setGuardando(true);
    try {
      await respaldosApi.guardarConfig(form);
      avisar('ok', 'Configuración guardada');
      setShowConfig(false);
      cargar(true);
    } catch (e) {
      avisar('error', errorDe(e, 'Error guardando configuración'));
    } finally {
      setGuardando(false);
    }
  };

  const respaldar = async (tenantId) => {
    setOcupado(tenantId);
    try {
      const r = tenantId === 'todos' ? await respaldosApi.respaldarTodos() : await respaldosApi.respaldarTenant(tenantId);
      avisar('ok', r.data.message);
      cargar(true);
    } catch (e) {
      avisar('error', errorDe(e, 'No se pudo iniciar el respaldo'));
    } finally {
      setOcupado(null);
    }
  };

  const descargar = async (log) => {
    setOcupado(`dl-${log.id}`);
    try {
      const r = await respaldosApi.descargar(log.id);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = log.file_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      let msg = 'Error descargando respaldo';
      try { msg = JSON.parse(await e.response?.data?.text())?.error || msg; } catch { /* binario */ }
      avisar('error', msg);
    } finally {
      setOcupado(null);
    }
  };

  const restaurar = async () => {
    try {
      const r = await respaldosApi.restaurar(aRestaurar.id, confirmacion);
      avisar('ok', r.data.message);
      setARestaurar(null);
      setConfirmacion('');
      cargar(true);
    } catch (e) {
      avisar('error', errorDe(e, 'No se pudo iniciar la restauración'));
    }
  };

  const eliminar = async () => {
    try {
      await respaldosApi.eliminar(aEliminar.id);
      avisar('ok', 'Respaldo eliminado');
      setAEliminar(null);
      cargar(true);
    } catch (e) {
      avisar('error', errorDe(e, 'Error eliminando respaldo'));
    }
  };

  const aplicarRetencion = async () => {
    setConfirmarVencidos(false);
    // Se cierra la configuración para que el resultado se vea (el aviso queda arriba de la página)
    setShowConfig(false);
    try {
      const r = await respaldosApi.aplicarRetencion();
      avisar('ok', r.data.message);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      cargar(true);
    } catch (e) {
      avisar('error', errorDe(e, 'Error aplicando retención'));
    }
  };

  const alternar = (id) => setSeleccion(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const eliminarVarios = async () => {
    if (!aEliminarVarios?.length) return;
    setBorrandoVarios(true);
    try {
      const r = await respaldosApi.eliminarVarios(aEliminarVarios.map(l => l.id));
      avisar(r.data.success ? 'ok' : 'error', r.data.message + (r.data.errores?.length ? ` ${r.data.errores.slice(0, 3).join(' · ')}` : ''));
      setSeleccion(new Set());
      setAEliminarVarios(null);
      cargar(true);
    } catch (e) {
      avisar('error', errorDe(e, 'Error eliminando respaldos'));
    } finally {
      setBorrandoVarios(false);
    }
  };

  const conRespaldo = resumen.filter(t => t.ultimoExitoso).length;
  const espacio = resumen.reduce((s, t) => s + (Number(t.espacioBytes) || 0), 0);

  return (
    <SuperadminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Respaldos</h1>
            <p className="text-gray-500">Base de datos + archivos de cada tenant, en Azure (blobbobkuantaapps / {config?.azureContainer || 'finance360'})</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => cargar()} className="p-2 bg-white border border-gray-200 hover:bg-gray-100 rounded-lg text-gray-700" title="Actualizar">
              <ArrowPathIcon className="w-5 h-5" />
            </button>
            <button onClick={abrirConfig} disabled={!config} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 hover:bg-gray-100 rounded-lg text-gray-700">
              <Cog6ToothIcon className="w-4 h-4" /> Configuración
            </button>
            <button
              onClick={() => respaldar('todos')}
              disabled={ocupado === 'todos' || !config?.azureConfigurado}
              className="flex items-center gap-2 px-4 py-2 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-lg disabled:opacity-40"
            >
              {ocupado === 'todos' ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <CircleStackIcon className="w-4 h-4" />}
              Respaldar todos
            </button>
          </div>
        </div>

        <AvisoRespaldoEmpresa analisis={analisis} onCambio={setAnalisis} onAviso={(a) => { setAviso(a); setTimeout(() => setAviso(null), 8000); }} />

        {aviso && (
          <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${aviso.tipo === 'ok' ? 'bg-green-50 text-green-700 border border-green-300' : 'bg-red-50 text-red-700 border border-red-300'}`}>
            {aviso.texto}
          </div>
        )}

        {config && !config.azureConfigurado && (
          <div className="mb-4 flex items-center gap-2 px-4 py-3 rounded-lg bg-yellow-50 border border-yellow-300 text-yellow-700 text-sm">
            <ExclamationTriangleIcon className="w-5 h-5 shrink-0" />
            Falta configurar el destino (SAS del contenedor en Azure). Sin eso no se respalda nada.
          </div>
        )}

        {/* Configuración */}
        {showConfig && form && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2"><Cog6ToothIcon className="w-5 h-5" /> Respaldos automáticos</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <label className="flex items-center gap-2 text-gray-700 md:col-span-2 lg:col-span-4">
                <input type="checkbox" checked={form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} className="w-4 h-4 rounded" />
                Respaldo automático de todos los tenants activos
              </label>
              <div>
                <label className="block text-sm text-gray-500 mb-1">Frecuencia</label>
                <select value={form.frequency} onChange={e => setForm({ ...form, frequency: e.target.value })} className={inputCls}>
                  {Object.entries(FRECUENCIA).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">Hora (Costa Rica)</label>
                <div className="flex items-center gap-2">
                  <input type="number" min="0" max="23" value={form.scheduleHour} onChange={e => setForm({ ...form, scheduleHour: e.target.value })} className={inputCls} />
                  <span className="text-gray-500">:</span>
                  <input type="number" min="0" max="59" value={form.scheduleMinute} onChange={e => setForm({ ...form, scheduleMinute: e.target.value })} className={inputCls} />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">Retención (días)</label>
                <input type="number" min="1" max="3650" value={form.retentionDays} onChange={e => setForm({ ...form, retentionDays: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">Contenedor</label>
                <input value={form.azureContainer} onChange={e => setForm({ ...form, azureContainer: e.target.value })} className={inputCls} />
              </div>
              <div className="md:col-span-2 lg:col-span-4">
                <label className="block text-sm text-gray-500 mb-1">
                  <CloudIcon className="w-4 h-4 inline mr-1" />
                  Cadena de conexión (SAS del contenedor) {config.azureConfigurado && <span className="text-green-700">· configurada, déjela vacía para no cambiarla</span>}
                </label>
                <input type="password" autoComplete="off" value={form.azureConnectionString} onChange={e => setForm({ ...form, azureConnectionString: e.target.value })}
                  placeholder="BlobEndpoint=https://blobbobkuantaapps.blob.core.windows.net/;SharedAccessSignature=..." className={inputCls} />
              </div>
            </div>
            <div className="flex justify-between mt-5 pt-4 border-t border-gray-200">
              <button onClick={() => setConfirmarVencidos(true)} className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-700 hover:bg-red-50 rounded-lg">
                <TrashIcon className="w-4 h-4" /> Borrar vencidos ahora
              </button>
              <div className="flex gap-2">
                <button onClick={() => setShowConfig(false)} className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg">Cancelar</button>
                <button onClick={guardarConfig} disabled={guardando} className="px-4 py-2 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-lg disabled:opacity-40">
                  {guardando ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Resumen */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Tarjeta icon={CircleStackIcon} color="bg-blue-50 text-blue-700" titulo="Tenants con respaldo" valor={`${conRespaldo} / ${resumen.length}`} />
          <Tarjeta icon={CloudIcon} color="bg-purple-50 text-purple-700" titulo="Espacio en Azure" valor={formatBytes(espacio)} />
          <Tarjeta icon={ClockIcon} color="bg-yellow-50 text-yellow-700" titulo="Próximo automático"
            valor={config?.enabled ? formatFecha(config.proxima) : 'Desactivado'} />
          <Tarjeta icon={config?.lastStatus === 'failed' ? XCircleIcon : CheckCircleIcon}
            color={config?.lastStatus === 'failed' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}
            titulo="Última corrida automática"
            valor={config?.lastRunAt ? `${formatFecha(config.lastRunAt)} · ${config.lastStatus === 'success' ? 'OK' : config.lastStatus === 'running' ? 'corriendo' : 'con fallos'}` : 'Nunca'} />
        </div>
        {config?.lastStatus === 'failed' && config.lastError && (
          <p className="mb-4 text-sm text-red-700">{config.lastError}</p>
        )}

        {/* Por tenant */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500"><ArrowPathIcon className="w-6 h-6 animate-spin mx-auto mb-2" />Cargando...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 bg-gray-50">
                    <th className="px-4 py-3 font-medium">Tenant</th>
                    <th className="px-4 py-3 font-medium">Último respaldo</th>
                    <th className="px-4 py-3 font-medium text-center">Estado</th>
                    <th className="px-4 py-3 font-medium text-right">Tamaño</th>
                    <th className="px-4 py-3 font-medium text-center">Exitosos</th>
                    <th className="px-4 py-3 font-medium text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {resumen.map(t => {
                    const historial = logs.filter(l => l.tenant_id === t.tenantId);
                    const abierto = expandido === t.tenantId;
                    const corriendo = historial.some(l => l.estado === 'en_curso' || l.restauracion_estado === 'en_curso');
                    return (
                      <Fragment key={t.tenantId}>
                        <tr className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <p className="text-gray-900 font-medium">{t.tenantName}</p>
                            <p className="text-gray-500 text-xs">{t.tenantSlug}{t.status !== 'active' && ` · ${t.status}`}</p>
                            <p className="text-xs mt-0.5">
                              <span className={t.destino === 'propio' ? 'text-indigo-700' : 'text-gray-500'}>
                                {t.destino === 'propio' ? `Azure propio (${t.destinoContenedor}${t.destinoCarpeta ? `/${t.destinoCarpeta}` : ''})` : 'Nube Kuanta'}
                              </span>
                              {Number(t.programacionPropia) === 1 && (
                                <span className="text-yellow-700">
                                  {' · '}{t.propiaEnabled ? `propia: ${FRECUENCIA[t.propiaFrequency]} ${String(t.propiaHora).padStart(2, '0')}:${String(t.propiaMinuto).padStart(2, '0')}, ${t.propiaRetencion} d` : 'automático apagado por la empresa'}
                                </span>
                              )}
                            </p>
                          </td>
                          <td className="px-4 py-3 text-gray-700">{formatFecha(t.ultimoExitoso)}</td>
                          <td className="px-4 py-3 text-center"><Estado estado={corriendo ? 'en_curso' : t.ultimoEstado} /></td>
                          <td className="px-4 py-3 text-right text-gray-700">{formatBytes(t.ultimoTamano)}</td>
                          <td className="px-4 py-3 text-center text-gray-700">{t.exitosos}/{t.total}</td>
                          <td className="px-4 py-3">
                            <div className="flex justify-end gap-1">
                              <button onClick={() => respaldar(t.tenantId)} disabled={ocupado === t.tenantId || corriendo || !config?.azureConfigurado}
                                title="Respaldar ahora" className="p-2 text-emerald-700 hover:bg-emerald-50 rounded-lg disabled:opacity-30">
                                {ocupado === t.tenantId ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <PlayIcon className="w-4 h-4" />}
                              </button>
                              <button onClick={() => setExpandido(abierto ? null : t.tenantId)} title="Historial" className="p-2 text-gray-700 hover:bg-gray-100 rounded-lg">
                                {abierto ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
                              </button>
                            </div>
                          </td>
                        </tr>
                        {abierto && (
                          <tr className="bg-gray-50">
                            <td colSpan={6} className="px-4 py-3">
                              {historial.length === 0 ? (
                                <p className="text-gray-500 text-center py-3">Sin respaldos</p>
                              ) : (
                                <>
                                {(() => {
                                  const elegibles = historial.filter(l => l.estado !== 'en_curso');
                                  const marcados = elegibles.filter(l => seleccion.has(l.id));
                                  const todos = elegibles.length > 0 && marcados.length === elegibles.length;
                                  return (
                                    <div className="flex items-center justify-between gap-3 mb-2 px-1">
                                      <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                                        <input type="checkbox" className="w-4 h-4 rounded" checked={todos}
                                          onChange={() => setSeleccion(prev => {
                                            const n = new Set(prev);
                                            elegibles.forEach(l => (todos ? n.delete(l.id) : n.add(l.id)));
                                            return n;
                                          })} />
                                        Seleccionar todos ({elegibles.length})
                                      </label>
                                      <button onClick={() => setAEliminarVarios(marcados)} disabled={marcados.length === 0}
                                        className="flex items-center gap-2 px-3 py-1.5 text-sm border border-red-300 text-red-700 hover:bg-red-50 rounded-lg disabled:opacity-40 whitespace-nowrap">
                                        <TrashIcon className="w-4 h-4" /> Eliminar seleccionados ({marcados.length})
                                      </button>
                                    </div>
                                  );
                                })()}
                                <div className="space-y-1.5 max-h-72 overflow-y-auto">
                                  {historial.map(l => (
                                    <div key={l.id} className={`flex flex-wrap items-center justify-between gap-2 bg-white border px-3 py-2 rounded-lg ${seleccion.has(l.id) ? 'border-red-300' : 'border-gray-200'}`}>
                                      <div className="flex flex-wrap items-center gap-3 text-gray-700">
                                        <input type="checkbox" className="w-4 h-4 rounded" checked={seleccion.has(l.id)} disabled={l.estado === 'en_curso'}
                                          onChange={() => alternar(l.id)} aria-label="Seleccionar respaldo" />
                                        <Estado estado={l.estado} error={l.error_message} />
                                        <span>{formatFecha(l.created_at)}</span>
                                        <span className={`text-xs px-2 py-0.5 rounded ${l.alcance === 'empresa' ? 'bg-indigo-50 text-indigo-700' : 'bg-red-50 text-red-700'}`}>
                                          {l.alcance === 'empresa' ? (l.emisor_nombre || 'Empresa') : 'Cuenta completa'}
                                        </span>
                                        <span className="text-xs px-2 py-0.5 bg-gray-100 rounded">{TIPO[l.tipo] || l.tipo}</span>
                                        <span className="text-gray-500">{formatBytes(l.size_bytes)}</span>
                                        <span className="text-gray-500 text-xs">{l.archivos_count ?? 0} archivos</span>
                                        {l.estado === 'exitoso' && l.blob_name && (
                                          <span className={`inline-flex items-center gap-1 text-xs ${l.destino === 'propio' ? 'text-indigo-700' : 'text-blue-700'}`}>
                                            <CloudIcon className="w-3.5 h-3.5" /> {l.destino === 'propio' ? 'Azure del cliente' : 'Nube Kuanta'} · {l.destino_cuenta}/{l.destino_contenedor}/{l.blob_name}
                                          </span>
                                        )}
                                        {l.creado_por && <span className="text-gray-500 text-xs">{l.creado_por}</span>}
                                        {l.restauracion_estado === 'en_curso' && <span className="text-blue-700 text-xs">restaurando…</span>}
                                        {l.restauracion_estado === 'fallida' && <span className="text-red-700 text-xs" title={l.restauracion_error || ''}>restauración fallida</span>}
                                        {l.restaurado_at && l.restauracion_estado === 'exitosa' && <span className="text-purple-700 text-xs">restaurado {formatFecha(l.restaurado_at)}</span>}
                                      </div>
                                      <div className="flex gap-1">
                                        <button onClick={() => descargar(l)} disabled={l.estado !== 'exitoso' || ocupado === `dl-${l.id}`} title="Descargar"
                                          className="p-1.5 text-blue-700 hover:bg-blue-50 rounded disabled:opacity-30">
                                          {ocupado === `dl-${l.id}` ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <ArrowDownTrayIcon className="w-4 h-4" />}
                                        </button>
                                        <button onClick={() => { setARestaurar(l); setConfirmacion(''); }} disabled={l.estado !== 'exitoso' || corriendo} title="Restaurar"
                                          className="p-1.5 text-yellow-700 hover:bg-yellow-50 rounded disabled:opacity-30">
                                          <ArrowUturnLeftIcon className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => setAEliminar(l)} disabled={l.estado === 'en_curso'} title="Eliminar"
                                          className="p-1.5 text-red-700 hover:bg-red-50 rounded disabled:opacity-30">
                                          <TrashIcon className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                </>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                  {resumen.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">No hay tenants</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Restaurar */}
      {aRestaurar && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setARestaurar(null)}>
          <div className="bg-white border border-gray-200 rounded-xl w-full max-w-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><ExclamationTriangleIcon className="w-5 h-5 text-yellow-700" /> Restaurar {aRestaurar.tenant_name}</h3>
              <button onClick={() => setARestaurar(null)} className="text-gray-500 hover:text-gray-900"><XMarkIcon className="w-5 h-5" /></button>
            </div>
            <div className="p-5 grid md:grid-cols-2 gap-5 text-sm">
              <div className="space-y-2 text-gray-700">
                <p>La base y los archivos de <span className="font-semibold text-gray-900">{aRestaurar.tenant_slug}</span> vuelven al <span className="font-semibold text-gray-900">{formatFecha(aRestaurar.created_at)}</span>.</p>
                <p className="text-red-700">Lo hecho después se pierde.</p>
                <p className="text-gray-500 text-xs">{aRestaurar.alcance === 'empresa' ? `Solo la empresa ${aRestaurar.emisor_nombre}; las demás no se tocan.` : 'TODAS las empresas de la cuenta.'}</p>
                <p className="text-gray-500 text-xs">Antes se toma automáticamente un respaldo del estado actual. Los consecutivos de Hacienda nunca retroceden.</p>
              </div>
              <label className="block text-gray-700">
                Escriba <span className="font-mono font-bold text-red-700">RESTAURAR</span>
                <input autoFocus value={confirmacion} onChange={e => setConfirmacion(e.target.value)} className={`${inputCls} mt-1 font-mono`} />
              </label>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200">
              <button onClick={() => setARestaurar(null)} className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg">Cancelar</button>
              <button onClick={restaurar} disabled={confirmacion.trim().toUpperCase() !== 'RESTAURAR'}
                className="px-4 py-2 border border-red-300 text-red-700 hover:bg-red-50 rounded-lg disabled:opacity-40">Restaurar</button>
            </div>
          </div>
        </div>
      )}

      {/* Eliminar varios */}
      {aEliminarVarios && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => !borrandoVarios && setAEliminarVarios(null)}>
          <div className="bg-white border border-gray-200 rounded-xl p-6 w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-3">
              <TrashIcon className="w-6 h-6 text-red-700" />
              <h3 className="text-lg font-semibold text-gray-900">Eliminar {aEliminarVarios.length} respaldo(s)</h3>
            </div>
            <p className="text-gray-700 text-sm mb-2">
              Se borran de Azure {aEliminarVarios.length} respaldo(s), {formatBytes(aEliminarVarios.reduce((s, l) => s + (Number(l.size_bytes) || 0), 0))} en total. No se puede deshacer.
            </p>
            <p className="text-gray-500 text-xs mb-4">Si entre ellos está el último respaldo exitoso de una empresa, esa empresa queda sin respaldo hasta el próximo.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setAEliminarVarios(null)} disabled={borrandoVarios} className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg">Cancelar</button>
              <button onClick={eliminarVarios} disabled={borrandoVarios} className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-700 hover:bg-red-50 rounded-lg disabled:opacity-40">
                {borrandoVarios && <ArrowPathIcon className="w-4 h-4 animate-spin" />} Eliminar {aEliminarVarios.length}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmar borrar vencidos */}
      {confirmarVencidos && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4" onClick={() => setConfirmarVencidos(false)}>
          <div className="bg-white border border-gray-200 rounded-xl p-6 w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-3">
              <TrashIcon className="w-6 h-6 text-red-700" />
              <h3 className="text-lg font-semibold text-gray-900">Borrar respaldos vencidos</h3>
            </div>
            <p className="text-gray-700 text-sm mb-4">
              Se borran de Azure los respaldos con más días que la retención de cada cuenta ({config?.retentionDays ?? form?.retentionDays ?? '—'} días en la configuración general). Nunca se borra el último respaldo exitoso de cada empresa. No se puede deshacer.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmarVencidos(false)} className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg">Cancelar</button>
              <button onClick={aplicarRetencion} className="px-4 py-2 border border-red-300 text-red-700 hover:bg-red-50 rounded-lg">Borrar vencidos</button>
            </div>
          </div>
        </div>
      )}

      {/* Eliminar */}
      {aEliminar && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setAEliminar(null)}>
          <div className="bg-white border border-gray-200 rounded-xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Eliminar respaldo</h3>
            </div>
            <p className="p-5 text-sm text-gray-700">
              Se borra de Azure el respaldo de <span className="text-gray-900 font-medium">{aEliminar.tenant_name}</span> del {formatFecha(aEliminar.created_at)} ({formatBytes(aEliminar.size_bytes)}). No se puede deshacer.
            </p>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200">
              <button onClick={() => setAEliminar(null)} className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg">Cancelar</button>
              <button onClick={eliminar} className="px-4 py-2 border border-red-300 text-red-700 hover:bg-red-50 rounded-lg">Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </SuperadminLayout>
  );
}
